import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Clock, Sparkles, Trophy, ArrowRight, AlertCircle, CheckCircle2, BookOpen, Headphones } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import Seo from '@/components/shared/Seo';
import { QuestionPassage, QuestionStem, AnswerOptionList, EXAM_TEXT_CLS } from '@/components/exam/QuestionDisplay';
import IeltsReadingExam from '@/components/exam/IeltsReadingExam';
import IeltsListeningExam from '@/components/exam/IeltsListeningExam';
import { ieltsApi, goToIeltsAuth } from '@/lib/ieltsApi';
import { toast } from 'sonner';

const SECTIONS_ORDER = ['listening','reading','writing','speaking'];
const SECTION_LABEL = { listening:'Listening', reading:'Reading', writing:'Writing', speaking:'Speaking' };

const IeltsMockTestPage = () => {
  const [needsLogin, setNeedsLogin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [mock, setMock] = useState(null);
  const [secIdx, setSecIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [question, setQuestion] = useState(null);
  const [selected, setSelected] = useState(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [results, setResults] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [writingCounts, setWritingCounts] = useState({});
  const [speakingCounts, setSpeakingCounts] = useState({});
  const [module, setModule] = useState('academic');
  const [mobile, setMobile] = useState(false);
  // Reading-section state: real-IELTS, passage-based
  const [readingPassageIdx, setReadingPassageIdx] = useState(0);
  const [readingPassage, setReadingPassage] = useState(null);
  const [readingQuestions, setReadingQuestions] = useState([]);
  const [readingAnswers, setReadingAnswers] = useState({});
  // Listening-section state: real-IELTS, set-based
  const [listeningSetIdx, setListeningSetIdx] = useState(0);
  const [listeningSet, setListeningSet] = useState(null);
  const [listeningQuestions, setListeningQuestions] = useState([]);
  const [listeningAnswers, setListeningAnswers] = useState({});
  const tickRef = useRef(null);

  useEffect(() => {
    ieltsApi.authMe()
      .then(() => Promise.all([
        ieltsApi.mockAvailability().then(setAvailability).catch(() => {}),
        ieltsApi.writingDashboard().then((d) => setWritingCounts(d.prompt_counts || {})).catch(() => {}),
        ieltsApi.speakingDashboard().then((d) => setSpeakingCounts(d.prompt_counts || {})).catch(() => {}),
      ]))
      .catch((e) => { if (e.status === 401) setNeedsLogin(true); })
      .finally(() => setAuthChecked(true));
  }, []);

  const currentSection = mock?.sections?.[secIdx] || SECTIONS_ORDER[secIdx];
  const sectionPlan = mock?.plan?.[currentSection] || [];
  const currentQid = sectionPlan[qIdx];
  const readingPassageList = mock?.reading_passages || [];
  const currentReadingPid = readingPassageList[readingPassageIdx];
  const listeningSetList = mock?.listening_sets || [];
  const currentListeningSid = listeningSetList[listeningSetIdx];

  // Single-question loader (non-reading, non-listening sections)
  useEffect(() => {
    if (phase !== 'running' || !mock?.mock_session_id || !currentQid) return;
    if (currentSection === 'reading' && readingPassageList.length > 0) return;
    if (currentSection === 'listening' && listeningSetList.length > 0) return;
    setSelected(null); setTextAnswer('');
    ieltsApi.mockQuestion(mock.mock_session_id, currentQid)
      .then((r) => setQuestion(r.question))
      .catch((e) => toast.error(e.message));
  }, [phase, mock, currentQid, currentSection, readingPassageList.length, listeningSetList.length]);

  // Reading passage loader
  useEffect(() => {
    if (phase !== 'running' || !mock?.mock_session_id) return;
    if (currentSection !== 'reading' || !currentReadingPid) return;
    setReadingPassage(null); setReadingQuestions([]); setReadingAnswers({});
    ieltsApi.mockReadingPassage(mock.mock_session_id, currentReadingPid)
      .then((r) => {
        setReadingPassage(r.passage);
        setReadingQuestions(r.questions || []);
      })
      .catch((e) => toast.error(e.message));
  }, [phase, mock, currentSection, currentReadingPid]);

  // Listening set loader
  useEffect(() => {
    if (phase !== 'running' || !mock?.mock_session_id) return;
    if (currentSection !== 'listening' || !currentListeningSid) return;
    setListeningSet(null); setListeningQuestions([]); setListeningAnswers({});
    ieltsApi.mockListeningSet(mock.mock_session_id, currentListeningSid)
      .then((r) => {
        setListeningSet(r.listening_set);
        setListeningQuestions(r.questions || []);
      })
      .catch((e) => toast.error(e.message));
  }, [phase, mock, currentSection, currentListeningSid]);

  useEffect(() => {
    if (phase !== 'running') return;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => { if (s <= 1) { clearInterval(tickRef.current); advanceSection(); return 0; } return s - 1; });
    }, 1000);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, secIdx]);

  const start = async () => {
    setPhase('starting');
    try {
      const m = await ieltsApi.mockStart({ module, sections: SECTIONS_ORDER });
      const plan = m?.plan || {}; const blueprint = m?.blueprint || {};
      const sections = (m?.sections?.length ? m.sections : SECTIONS_ORDER).filter((s) => Array.isArray(plan[s]) && plan[s].length > 0);
      if (!sections.length) { toast.error('No questions available yet. Please contact admin.'); setPhase('idle'); return; }
      setMock({ ...m, sections, plan, blueprint, reading_passages: m?.reading_passages || [], listening_sets: m?.listening_sets || [] });
      setSecIdx(0); setQIdx(0); setReadingPassageIdx(0); setListeningSetIdx(0);
      setSecondsLeft((blueprint[sections[0]]?.duration_min || 30) * 60);
      setPhase('running');
    } catch (e) {
      if (e.status === 401) setNeedsLogin(true); else toast.error(e.message || 'Could not start mock');
      setPhase('idle');
    }
  };

  const submitAnswer = async () => {
    if (!question || !mock?.mock_session_id) return;
    const qt = question.question_type || 'mcq';
    const body = { question_id: question.id, seconds_spent: 0, is_mock: true, mock_session_id: mock.mock_session_id };
    if (qt === 'mcq' || qt === 'multiple_choice_one_answer') body.selected_option = selected;
    else body.text_answer = textAnswer;
    try { await ieltsApi.practiceSubmit(body); } catch (e) { /* keep going */ }
    nextQuestion();
  };

  const submitReadingPassage = async () => {
    if (!readingPassage || !mock?.mock_session_id) return;
    const answers = readingQuestions.map((q) => ({
      question_id: q.id,
      selected_option: readingAnswers[q.id]?.selected_option,
      text_answer: readingAnswers[q.id]?.text_answer,
    }));
    try {
      await ieltsApi.readingSubmitBatch({
        passage_id: readingPassage.passage_id,
        answers,
        is_mock: true,
        mock_session_id: mock.mock_session_id,
      });
    } catch (e) {
      // keep going — mock should never get stuck on a single passage save failure
    }
    advanceReadingPassage();
  };

  const advanceReadingPassage = () => {
    if (readingPassageIdx + 1 < readingPassageList.length) {
      setReadingPassageIdx(readingPassageIdx + 1);
    } else {
      advanceSection();
    }
  };

  const submitListeningSet = async () => {
    if (!listeningSet || !mock?.mock_session_id) return;
    const answers = listeningQuestions.map((q) => ({
      question_id: q.id,
      selected_option: listeningAnswers[q.id]?.selected_option,
      text_answer: listeningAnswers[q.id]?.text_answer,
    }));
    try {
      await ieltsApi.listeningSubmitBatch({
        listening_set_id: listeningSet.listening_set_id,
        answers,
        is_mock: true,
        mock_session_id: mock.mock_session_id,
      });
    } catch (e) { /* keep going */ }
    advanceListeningSet();
  };

  const advanceListeningSet = () => {
    if (listeningSetIdx + 1 < listeningSetList.length) {
      setListeningSetIdx(listeningSetIdx + 1);
    } else {
      advanceSection();
    }
  };

  const onReadingChangeAnswer = (qid, partial) => {
    setReadingAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...partial } }));
  };
  const onListeningChangeAnswer = (qid, partial) => {
    setListeningAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...partial } }));
  };

  const nextQuestion = () => {
    if (qIdx + 1 < sectionPlan.length) setQIdx(qIdx + 1); else advanceSection();
  };

  const advanceSection = async () => {
    const sections = mock?.sections || [];
    if (secIdx + 1 < sections.length) {
      const next = sections[secIdx + 1];
      setSecIdx(secIdx + 1); setQIdx(0); setReadingPassageIdx(0); setListeningSetIdx(0);
      setSecondsLeft((mock?.blueprint?.[next]?.duration_min || 30) * 60);
    } else if (mock?.mock_session_id) {
      setPhase('finalizing');
      try { const r = await ieltsApi.mockFinalize(mock.mock_session_id); setResults(r); setPhase('done'); }
      catch (e) { toast.error(e.message); setPhase('idle'); }
    } else { setPhase('idle'); }
  };

  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;
  const secDurSec = mock?.blueprint?.[currentSection]?.duration_min ? mock.blueprint[currentSection].duration_min * 60 : 1;
  const timePct = phase === 'running' ? Math.max(0,(secondsLeft / secDurSec) * 100) : 0;

  if (!authChecked) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-7 w-7 animate-spin text-red-700" /></div>;
  if (needsLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center mx-auto mb-4"><Sparkles className="h-5 w-5 text-[#D3D86F]" /></div>
          <h1 className="text-2xl font-bold mb-2">Sign in to take a mock test</h1>
          <Button onClick={() => goToIeltsAuth('/ielts/mock-test')} className="w-full bg-black hover:bg-gray-900 text-white rounded-full h-11">Sign in with Google</Button>
        </CardContent></Card>
      </div>
    );
  }

  const qt = question?.question_type || 'mcq';
  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="ielts-mock">
      <Seo title="IELTS Mock Test · Winhouse" canonical="/ielts/mock-test" />

      {phase === 'running' && mock?.sections?.length > 0 && (
        <div className="sticky top-0 z-30 bg-black text-white border-b border-white/10 backdrop-blur">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Badge className="bg-[#D3D86F] text-black hover:bg-[#D3D86F] flex-shrink-0">Section {secIdx + 1} of {mock.sections.length}</Badge>
              <span className="text-sm font-semibold truncate">{SECTION_LABEL[currentSection] || currentSection}</span>
              {currentSection === 'reading' && readingPassageList.length > 0
                ? <span className="text-xs text-white/70 hidden sm:inline">Passage {readingPassageIdx + 1} / {readingPassageList.length}</span>
                : currentSection === 'listening' && listeningSetList.length > 0
                  ? <span className="text-xs text-white/70 hidden sm:inline">Set {listeningSetIdx + 1} / {listeningSetList.length}</span>
                  : <span className="text-xs text-white/70 hidden sm:inline">Question {qIdx + 1} / {sectionPlan.length}</span>
              }
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:block w-28 h-1.5 bg-white/15 rounded-full overflow-hidden">
                <div className={`h-full transition-all ${secondsLeft < 60 ? 'bg-red-500' : 'bg-[#D3D86F]'}`} style={{ width: `${timePct}%` }} />
              </div>
              <span className={`flex items-center gap-1.5 font-mono font-bold text-base ${secondsLeft < 60 ? 'text-red-400' : 'text-white'}`} data-testid="ielts-mock-timer">
                <Clock className="h-4 w-4" /> {fmt(secondsLeft)}
              </span>
            </div>
          </div>
        </div>
      )}

      {phase !== 'running' && <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />}

      <main className={`flex-1 mx-auto px-4 sm:px-6 pb-10 w-full ${
        phase === 'running' && ((currentSection === 'reading' && readingPassageList.length) || (currentSection === 'listening' && listeningSetList.length))
          ? 'max-w-7xl' : 'max-w-3xl'
      } ${phase === 'running' ? 'pt-6' : 'pt-24 sm:pt-28'}`}>
        {phase === 'idle' && (
          <>
            <div className="mb-6">
              <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">Mock Test</div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Full IELTS Mock</h1>
              <p className="text-gray-600 text-sm mt-1">Four sections · ~2h 45min · Band 0–9 estimate.</p>
            </div>
            <Card className="border-gray-200 mb-5">
              <CardContent className="p-6 sm:p-8">
                <div className="mb-5">
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Module</label>
                  <select className="border border-gray-200 rounded-lg p-2.5 bg-white w-full text-sm max-w-xs"
                    value={module} onChange={(e) => setModule(e.target.value)}>
                    <option value="academic">Academic</option>
                    <option value="general">General Training</option>
                  </select>
                </div>
                <div className="grid sm:grid-cols-4 gap-3 mb-7">
                  {SECTIONS_ORDER.map((s, i) => {
                    // Writing & Speaking use their own prompt banks — not the legacy question count.
                    if (s === 'writing') {
                      const t1 = module === 'general' ? (writingCounts.general_task1 || 0) : (writingCounts.academic_task1 || 0);
                      const t2 = module === 'general' ? (writingCounts.general_task2 || 0) : (writingCounts.academic_task2 || 0);
                      const ready = t1 > 0 && t2 > 0;
                      return (
                        <div key={s} className={`rounded-xl p-4 border ${ready ? 'bg-[#FAF9F6] border-gray-100' : 'bg-red-50 border-red-200'}`} data-testid={`mock-row-${s}`}>
                          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Section {i+1}</div>
                          <div className="font-bold text-sm">{SECTION_LABEL[s]}</div>
                          <div className="text-xs text-gray-600 mt-1">Task 1 + Task 2 · AI band</div>
                          <div className={`text-[11px] mt-1.5 font-medium ${ready ? 'text-emerald-700' : 'text-red-700'}`}>
                            {ready ? `${t1} Task 1 + ${t2} Task 2 available ✓` : 'Not enough prompts'}
                          </div>
                        </div>
                      );
                    }
                    if (s === 'speaking') {
                      const p1 = speakingCounts.part_1 || 0;
                      const p2 = speakingCounts.part_2 || 0;
                      const p3 = speakingCounts.part_3 || 0;
                      const ready = p1 > 0 && p2 > 0 && p3 > 0;
                      return (
                        <div key={s} className={`rounded-xl p-4 border ${ready ? 'bg-[#FAF9F6] border-gray-100' : 'bg-red-50 border-red-200'}`} data-testid={`mock-row-${s}`}>
                          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Section {i+1}</div>
                          <div className="font-bold text-sm">{SECTION_LABEL[s]}</div>
                          <div className="text-xs text-gray-600 mt-1">3 parts · AI band</div>
                          <div className={`text-[11px] mt-1.5 font-medium ${ready ? 'text-emerald-700' : 'text-red-700'}`}>
                            {ready ? `${p1} P1 + ${p2} P2 + ${p3} P3 available ✓` : 'Not enough prompts'}
                          </div>
                        </div>
                      );
                    }
                    // Reading / Listening — auto-graded question bank
                    const req = availability?.blueprint?.[s]?.count;
                    const av = availability?.counts?.[s] ?? 0;
                    const empty = availability && av === 0;
                    const short = availability && req && av < req;
                    return (
                      <div key={s} className={`rounded-xl p-4 border ${empty ? 'bg-red-50 border-red-200' : short ? 'bg-amber-50 border-amber-200' : 'bg-[#FAF9F6] border-gray-100'}`} data-testid={`mock-row-${s}`}>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Section {i+1}</div>
                        <div className="font-bold text-sm">{SECTION_LABEL[s]}</div>
                        <div className="text-xs text-gray-600 mt-1">{req ?? '—'} questions</div>
                        {availability && <div className={`text-[11px] mt-1.5 font-medium ${empty ? 'text-red-700' : short ? 'text-amber-800' : 'text-emerald-700'}`}>
                          {empty ? 'Not enough questions' : short ? `${av} of ${req} available` : `${av} available ✓`}
                        </div>}
                      </div>
                    );
                  })}
                </div>
                <div className="space-y-2.5 text-sm text-gray-700 mb-7">
                  <div className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-red-700 mt-0.5 flex-shrink-0" /><span>Answers lock once submitted.</span></div>
                  <div className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-red-700 mt-0.5 flex-shrink-0" /><span>Sections auto-advance when the timer reaches zero.</span></div>
                  <div className="flex items-start gap-2"><AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" /><span>Band estimate based on auto-graded questions. Writing &amp; Speaking receive AI/manual review feedback in their dedicated mocks.</span></div>
                </div>
                <Button onClick={start}
                  disabled={availability && ['reading', 'listening'].every((s) => (availability.counts?.[s] ?? 0) === 0)}
                  className="w-full bg-red-700 hover:bg-red-800 text-white rounded-full h-12 text-base disabled:opacity-50"
                  data-testid="ielts-mock-start">
                  Begin Reading + Listening Mock <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2 items-center justify-between">
                  <span className="text-xs text-gray-500">Writing &amp; Speaking are timed mocks of their own:</span>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => window.location.assign(`/ielts/writing/mock?module=${module}`)} variant="outline"
                      className="rounded-full h-9 px-4 text-xs border-black hover:bg-black hover:text-white"
                      data-testid="ielts-mock-writing-link">
                      Writing Mock <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                    <Button onClick={() => window.location.assign('/ielts/speaking/mock')} variant="outline"
                      className="rounded-full h-9 px-4 text-xs border-black hover:bg-black hover:text-white"
                      data-testid="ielts-mock-speaking-link">
                      Speaking Mock <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {phase === 'starting' && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-red-700 mb-3" />
            <p className="text-gray-700 font-semibold">Preparing your mock test…</p>
          </div>
        )}

        {phase === 'running' && currentSection === 'listening' && listeningSetList.length > 0 && (
          <div data-testid="ielts-mock-listening">
            <div className="mb-3 flex items-center gap-2 text-sm text-gray-700">
              <Headphones className="h-4 w-4 text-red-700" />
              <span className="font-semibold">Listening · Set {listeningSetIdx + 1} of {listeningSetList.length}</span>
            </div>
            {listeningSet ? (
              <>
                <IeltsListeningExam
                  listeningSet={listeningSet}
                  questions={listeningQuestions}
                  answers={listeningAnswers}
                  onChangeAnswer={onListeningChangeAnswer}
                  locked={false}
                  hideTranscriptDuringExam={true}
                />
                <div className="mt-5 flex gap-2 justify-end">
                  <Button onClick={advanceListeningSet} variant="outline" className="rounded-full h-11 px-5 border-gray-300">
                    Skip set
                  </Button>
                  <Button onClick={submitListeningSet}
                    className="bg-black hover:bg-gray-900 text-white rounded-full h-11 px-6"
                    data-testid="ielts-mock-listening-submit">
                    Submit & Next set <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex justify-center py-20">
                <Loader2 className="h-7 w-7 animate-spin text-red-700" />
              </div>
            )}
          </div>
        )}

        {phase === 'running' && currentSection === 'reading' && readingPassageList.length > 0 && (
          <div data-testid="ielts-mock-reading">
            <div className="mb-3 flex items-center gap-2 text-sm text-gray-700">
              <BookOpen className="h-4 w-4 text-red-700" />
              <span className="font-semibold">Reading · Passage {readingPassageIdx + 1} of {readingPassageList.length}</span>
            </div>
            {readingPassage ? (
              <>
                <IeltsReadingExam
                  passage={readingPassage}
                  questions={readingQuestions}
                  answers={readingAnswers}
                  onChangeAnswer={onReadingChangeAnswer}
                  locked={false}
                />
                <div className="mt-5 flex gap-2 justify-end">
                  <Button onClick={advanceReadingPassage} variant="outline" className="rounded-full h-11 px-5 border-gray-300">
                    Skip passage
                  </Button>
                  <Button onClick={submitReadingPassage}
                    className="bg-black hover:bg-gray-900 text-white rounded-full h-11 px-6"
                    data-testid="ielts-mock-reading-submit">
                    Submit & Next passage <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex justify-center py-20">
                <Loader2 className="h-7 w-7 animate-spin text-red-700" />
              </div>
            )}
          </div>
        )}

        {phase === 'running' && currentSection !== 'reading' && currentSection !== 'listening' && question && (
          <Card key={question.id} className="border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                <Badge variant="outline" className="capitalize">{(question.question_type || '').replace(/_/g,' ')}</Badge>
                <Badge variant="outline" className="capitalize">{question.topic.replace(/_/g,' ')}</Badge>
              </div>
              {question.passage_text && <QuestionPassage text={question.passage_text} />}
              {question.transcript_text && <QuestionPassage text={`Transcript:\n${question.transcript_text}`} />}
              {question.audio_url && <audio controls src={question.audio_url} className="w-full mb-5" />}
              <QuestionStem text={question.question_text} />

              {qt === 'mcq' && (
                <AnswerOptionList question={question} selectedLetter={selected}
                  onSelect={(k) => setSelected(k)} testIdPrefix="ielts-mock-opt" />
              )}
              {qt !== 'mcq' && (
                <textarea value={textAnswer} onChange={(e) => setTextAnswer(e.target.value)}
                  rows={qt === 'essay_prompt' ? 14 : 4} placeholder="Type your answer..."
                  className={`w-full border-2 border-gray-200 focus:border-red-700 rounded-xl p-3 text-sm outline-none mb-6 ${EXAM_TEXT_CLS}`}
                  data-testid="ielts-mock-text-input" />
              )}

              <div className="flex gap-2">
                <Button onClick={submitAnswer}
                  className="flex-1 bg-black hover:bg-gray-900 text-white rounded-full h-12"
                  data-testid="ielts-mock-submit">
                  Submit & Next <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button variant="outline" onClick={nextQuestion} className="rounded-full h-12 px-5 border-gray-300">Skip</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {phase === 'finalizing' && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-red-700 mb-3" />
            <p className="text-gray-600 text-sm">Calculating your band…</p>
          </div>
        )}

        {phase === 'done' && results && (
          <div data-testid="ielts-mock-results">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 rounded-full px-3 py-1 text-xs uppercase tracking-wider font-semibold mb-3">
                <Trophy className="h-3.5 w-3.5" /> Mock complete
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Your estimated band</h1>
            </div>
            <Card className="bg-black text-white border-0 mb-6 overflow-hidden relative">
              <div className="absolute inset-0 opacity-50" style={{ background: 'radial-gradient(50% 60% at 50% 0%, rgba(220,38,38,0.45) 0%, transparent 70%)' }} />
              <CardContent className="relative p-8 text-center">
                <div className="text-xs uppercase tracking-[0.25em] text-white/60 mb-3">Band · 0–9</div>
                <div className="text-7xl sm:text-8xl font-bold text-[#D3D86F]">{results.estimated_band}</div>
                <div className="text-xs text-white/50 mt-3">{results.note}</div>
              </CardContent>
            </Card>
            <Card className="mb-6"><CardContent className="p-6">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-4">Section breakdown (auto-graded)</div>
              <div className="space-y-5">
                {Object.entries(results.breakdown).map(([s, b]) => (
                  <div key={s}>
                    <div className="flex items-center justify-between mb-1.5 text-sm">
                      <span className="font-semibold">{SECTION_LABEL[s]}</span>
                      <span className="text-gray-700">{b.correct} / {b.graded} graded · <span className="font-bold">{b.percent_correct}%</span></span>
                    </div>
                    <Progress value={b.percent_correct || 0} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent></Card>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => { setPhase('idle'); setMock(null); setResults(null); }} variant="outline" className="rounded-full h-11 px-6">Take another mock</Button>
              <Button onClick={() => window.location.assign('/ielts/dashboard')} className="bg-red-700 hover:bg-red-800 text-white rounded-full h-11 px-6">Back to dashboard <ArrowRight className="h-4 w-4 ml-1" /></Button>
            </div>
          </div>
        )}
      </main>
      {phase !== 'running' && <Footer />}
    </div>
  );
};
export default IeltsMockTestPage;
